/**
 * @file おんがくタブの「きき分けあそび」親向けカード (epic_24 §4.3)。
 * @what: 既定は「きょうの実施・連続日数・和音のみちのり (9 枠チップ)・はじめる」。
 *   数字の進捗バーや目標は出さない (epic_23 retention レンズ「遅れの表示を出さない」、S6)。
 *   くわしく (初期展開、代表 2026-08-26。閉じることは可) に 4 段のみちのりと時期の目安・
 *   和音別の直近正答率・次の見込み・無音疑い・原典と音源の表記を置く。
 * @why: 毎日の導線 (はじめる 1 タップ) と、親の現状把握 (AC-5) を 1 か所にまとめる。
 *   全体像 (9 枠チップ + みちのり) を出すのは「最初は 1 和音だけで全体が見えず不安」
 *   という代表フィードバック (2026-08-25) 対応。段階の遅れでなく道のり全体の見取り図
 *   なので retention レンズと両立する。時期の目安の文言は原典の進め方 (R6/R7 の 2 週間)
 *   と土台カタログ pitch_ear の「訓練 2〜3 年」に合わせており、変えるときは両方を見る。
 * @failure: 取得失敗は「再読み込み」ボタンつきの 1 行 (詳細は console)。学習者未選択は
 *   案内文。みちのりは静的で、進んでいなくても常に全段を表示する (隠すと全体像にならない)。
 */

import { Link } from "react-router-dom";
import { KIKIWAKE_CHORDS } from "../model/kikiwake/chords";
import {
  kikiwakeChordLabel,
  useKikiwakeOverview,
} from "../hooks/useKikiwakeOverview";
import "./study.css";

export function KikiwakeCard({
  familyId,
  personId,
  sourceHrefBase,
}: {
  familyId: string;
  personId: string | null;
  /** 本だな (source) へのリンクの前置き (例: /study/{fid}/sources)。 */
  sourceHrefBase: string;
}) {
  const { state, reload } = useKikiwakeOverview(familyId, personId);

  return (
    <section className="study-section">
      <h2 className="study-section-title">
        <span aria-hidden="true">🎧</span> きき分けあそび
      </h2>
      <p className="study-section-help">
        和音を聴いて色ハタで答える毎日のあそびです。お子さんと一緒に、1 回 数分で終わります。
      </p>
      {!personId ? (
        <p className="study-muted">お子さんを選ぶと出ます。</p>
      ) : state.status === "loading" ? (
        <p className="study-muted">読み込み中…</p>
      ) : state.status === "error" ? (
        <p className="study-muted">
          読み込めませんでした。
          <button type="button" className="study-link-btn" onClick={reload}>
            再読み込み
          </button>
        </p>
      ) : (
        <div className="study-kikiwake-card">
          <div className="study-kikiwake-status">
            <span>
              きょうは {state.overview.todayCount > 0 ? `${state.overview.todayCount} 回` : "まだ"}
            </span>
            {state.overview.streakDays > 1 ? <span>連続 {state.overview.streakDays} 日</span> : null}
            <span className="study-kikiwake-chips" aria-label="和音のみちのり">
              {KIKIWAKE_CHORDS.map((c) => {
                const isActive = state.overview.state.active.includes(c.id);
                return isActive ? (
                  <span
                    key={c.id}
                    className="study-kikiwake-chip"
                    style={{ backgroundColor: c.css }}
                    title={kikiwakeChordLabel(c.id)}
                  />
                ) : (
                  <span key={c.id} className="study-kikiwake-chip study-kikiwake-chip--future" title="これから" />
                );
              })}
            </span>
          </div>
          {state.overview.forked ? (
            <p className="study-muted">別の端末で実施中かもしれません（記録は失われません）。</p>
          ) : null}
          {state.overview.mutedSuspectRecent ? (
            <p className="study-muted">音が鳴っていなかったかもしれない回があります。端末が消音になっていないか確かめてください。</p>
          ) : null}
          <Link className="study-primary-btn study-kikiwake-start" to={`/study/${familyId}/kikiwake/${personId}`}>
            はじめる
          </Link>
          <details className="study-kikiwake-details" open>
            <summary>くわしく</summary>
            <ol className="study-kikiwake-roadmap">
              <li className="is-current">
                白鍵の 9 和音を 1 個ずつ（いま {state.overview.state.active.length} 個め）
              </li>
              <li>黒鍵の 5 和音</li>
              <li>色の名前から音名へ</li>
              <li>単音のききわけ・仕上げ</li>
            </ol>
            <p>
              1 つの和音に 2 週間以上かけます（まちがいが 2 週間出なくなったら、次の和音がひとつ増えます）。
              白鍵の 9 個がそろうまで早くて半年、全体では 2〜3 年かけて身につけるのがふつうです。
              黒鍵から先は、白鍵がそろってから始めます。
            </p>
            {state.overview.state.care ? (
              <p>
                いまは <strong>{kikiwakeChordLabel(state.overview.state.care.isolated)}</strong> をまぜて出すのを休み、
                練習の最後にまとめて聴いています（{kikiwakeChordLabel(state.overview.state.care.mixed)} と聴き分けやすくするためです）。
              </p>
            ) : null}
            {state.overview.nextEstimateDays !== null ? (
              <p>
                つぎの和音は、毎日つづけたとき
                {state.overview.nextEstimateDays <= 0 ? " もうすぐ" : ` あと ${state.overview.nextEstimateDays} 日ぐらい`}
                で増える見込みです。
              </p>
            ) : (
              <p>白鍵の 9 和音がぜんぶそろっています。</p>
            )}
            {state.overview.perChord.some((c) => c.asked > 0) ? (
              <table className="study-kikiwake-table">
                <thead>
                  <tr>
                    <th>和音</th>
                    <th>直近 30 日</th>
                  </tr>
                </thead>
                <tbody>
                  {state.overview.perChord.map((c) => (
                    <tr key={c.id}>
                      <td>{kikiwakeChordLabel(c.id)}</td>
                      <td>{c.asked > 0 ? `${c.ok} / ${c.asked} 問` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            <p className="study-kikiwake-links">
              <Link to={`${sourceHrefBase}/onkan-program?page=58`}>本の進め方のページ</Link>
              <a href="/kikiwake/NOTICE.txt" target="_blank" rel="noreferrer">
                音源のライセンス表記
              </a>
            </p>
          </details>
        </div>
      )}
    </section>
  );
}
